import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UserRoleForm from '../components/UserRoleForm';
import '../index.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser, refreshUserData } = useAuth();
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get payment details from URL parameters
  const merchantTradeNo = searchParams.get('merchantTradeNo');
  const planType = searchParams.get('planType') || 'pro';
  const billingPeriod = searchParams.get('billingPeriod') || 'monthly';

  useEffect(() => {
    // Verify payment status
    verifyPayment();
  }, [merchantTradeNo]);

  const verifyPayment = async () => {
    try {
      // In a real implementation, you would verify the payment with your backend
      // For now, we'll simulate payment verification
      if (merchantTradeNo) {
        // Simulate API call to verify payment
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Check if payment was successful (in real app, check with backend)
        const paymentStatus = localStorage.getItem(`payment_${merchantTradeNo}`);

        if (paymentStatus === 'completed') {
          setPaymentVerified(true);
        } else {
          // For demo purposes, assume payment is successful if we have merchantTradeNo
          setPaymentVerified(true);
        }
      } else {
        setError('Invalid payment reference');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSubmit = async (selectedRole) => {
    try {
      setLoading(true);

      // Update subscription in backend
      const token = localStorage.getItem('token');
      const subscriptionResponse = await fetch('/api/users/subscription', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planId: planType,
          planStatus: 'active',
          billingPeriod: billingPeriod,
          paymentMethod: 'binance_pay',
          subscribedAt: new Date().toISOString()
        })
      });

      if (!subscriptionResponse.ok) {
        throw new Error('Failed to update subscription');
      }

      const subscriptionData = await subscriptionResponse.json();

      // Update user data with role and plan information
      const updatedUserData = {
        ...user,
        role: selectedRole,
        planStatus: planType,
        billingPeriod: billingPeriod,
        planId: merchantTradeNo,
        subscribedAt: new Date().toISOString(),
        paymentMethod: 'binance_pay',
        subscriptionStatus: 'active'
      };

      // Update current user in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUserData));

      // Update auth context
      if (updateUser) {
        updateUser(updatedUserData);
      }

      // Refresh user data from backend to ensure consistency
      await refreshUserData();

      // Mark payment as completed
      localStorage.setItem(`payment_${merchantTradeNo}`, 'completed');

      // Redirect to workspace page based on role
      if (selectedRole === 'freelancer') {
        navigate('/FreelancerWorkSpace', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      } else if (selectedRole === 'student') {
        navigate('/StudentWorkSpace', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      } else if (selectedRole === 'content_creator') {
        navigate('/CreatorWorkSpace', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      } else if (selectedRole === 'influencer') {
        navigate('/InfluencerWorkSpace', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      } else if (selectedRole === 'entrepreneur') {
        navigate('/EntrepreneurWorkSpace', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      } else {
        navigate('/profile', {
          state: {
            message: `Welcome to ${planType} plan! Your role has been set to ${selectedRole}.`,
            planUpgraded: true
          }
        });
      }

    } catch (err) {
      console.error('Error updating user role:', err);
      setError('Failed to save your role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-success-container">
        <div className="loading-spinner">
          <h2>Verifying Payment...</h2>
          <div className="spinner"></div>
          <p>Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-container">
        <div className="error-message">
          <h2>Payment Verification Failed</h2>
          <p>{error}</p>
          <button
            className="btn"
            onClick={() => navigate('/pricing')}
          >
            Return to Pricing
          </button>
        </div>
      </div>
    );
  }

  if (!paymentVerified) {
    return (
      <div className="payment-success-container">
        <div className="error-message">
          <h2>Payment Not Verified</h2>
          <p>Your payment could not be verified. Please contact support if you believe this is an error.</p>
          <button
            className="btn"
            onClick={() => navigate('/pricing')}
          >
            Return to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container">
      <div className="success-header">
        <h1>🎉 Payment Successful!</h1>
        <p className="success-message">
          Welcome to the <strong>{planType}</strong> plan!
          <br />
          To complete your setup, please tell us a bit more about yourself.
        </p>
      </div>

      <div className="role-form-section">
        <UserRoleForm onSubmit={handleRoleSubmit} user={user} />
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Saving your information...</p>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
